import React, { useState, useEffect } from 'react';
import { IoShareOutline } from 'react-icons/io5';
import { CiSquarePlus } from 'react-icons/ci';
import { useTranslation } from 'react-i18next';

const DISMISS_KEY = 'pwa_banner_dismissed';

/**
 * Dismissible top banner that guides iOS users through the "Add to Home Screen"
 * flow. Appears only when:
 *   - The device is iOS (iPhone / iPad / iPod)
 *   - The app is NOT already running in standalone / PWA mode
 *   - The user hasn't previously dismissed the banner
 *
 * Dismissal is persisted to localStorage so it survives page reloads.
 * The banner never blocks navigation or the login form.
 */
const InstallationPrompt = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const alreadyDismissed = localStorage.getItem(DISMISS_KEY) === 'true';
    if (!alreadyDismissed) {
      setVisible(true);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div style={styles.banner} role="banner" aria-label={t('Install app banner')}>
      {/* ── Main bar ─────────────────────────────────────────────── */}
      <div style={styles.row}>
        {/* App icon */}
        <img
          src="/icon512_maskable.png"
          alt="App icon"
          style={styles.icon}
        />

        {/* Message */}
        <span style={styles.message}>
          {t('Add to Home Screen for the best experience.')}
        </span>

        {/* Expand / collapse instructions */}
        <button
          style={styles.howBtn}
          onClick={() => setExpanded(prev => !prev)}
          aria-expanded={expanded}
          aria-label={t('Toggle install instructions')}
        >
          {expanded ? t('Less') : t('How?')}
        </button>

        {/* Dismiss */}
        <button
          style={styles.closeBtn}
          onClick={handleDismiss}
          aria-label={t('Dismiss install banner')}
        >
          ✕
        </button>
      </div>

      {/* ── Expanded instructions ────────────────────────────────── */}
      {expanded && (
        <ol style={styles.steps}>
          <li>
            {t('Tap the')} <IoShareOutline size={16} color="#664d03" style={{ verticalAlign: 'middle' }} />{' '}
            {t('Share icon at the bottom of Safari.')}
          </li>
          <li>
            {t('Scroll down and tap')}{' '}
            <strong>{t('Add to Home Screen')}</strong>{' '}
            <CiSquarePlus size={16} color="#664d03" style={{ verticalAlign: 'middle' }} />.
          </li>
          <li>
            {t('Tap')} <strong>{t('Add')}</strong> {t('in the top-right corner.')}
          </li>
        </ol>
      )}
    </div>
  );
};

// ── Inline styles (no CSS file dependency) ───────────────────────────────────

const styles = {
  banner: {
    backgroundColor: '#fff3cd',   // Bootstrap warning yellow — clearly distinct from the dark header
    color: '#664d03',
    borderBottom: '1px solid #ffda6a',
    padding: '8px 16px',
    fontSize: '14px',
    lineHeight: 1.4,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'nowrap',
  },
  icon: {
    width: 32,
    height: 32,
    borderRadius: 7,
    flexShrink: 0,
  },
  message: {
    flex: 1,
    minWidth: 0,
  },
  howBtn: {
    background: 'rgba(0,0,0,0.08)',
    border: '1px solid rgba(0,0,0,0.2)',
    color: '#664d03',
    borderRadius: 4,
    padding: '3px 10px',
    fontSize: 13,
    cursor: 'pointer',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#664d03',
    fontSize: 18,
    cursor: 'pointer',
    lineHeight: 1,
    padding: '0 4px',
    flexShrink: 0,
    opacity: 0.7,
  },
  steps: {
    marginTop: 8,
    marginBottom: 4,
    paddingLeft: 24,
    color: '#664d03',
    lineHeight: 1.8,
  },
};

export default InstallationPrompt;
